import { DriverEngine } from './engines/driver.engine.js';
import '@/assets/css/tour.css';

/**
 * Agnostic Tour Service.
 * Orchestrates tour configuration, persistence, i18n, and delegates execution
 * to an interchangeable tour engine implementation.
 */
class TourService {
  constructor(engine = null) {
    this.engine = engine || new DriverEngine();
  }

  /**
   * Swap the underlying engine at runtime if needed
   * @param {TourEngineInterface} engine
   */
  setEngine(engine) {
    if (this.engine && this.engine.isActive()) {
      this.engine.destroy();
    }
    this.engine = engine;
  }

  /**
   * Check if a tour has already been completed/seen
   * @param {string} storageKey
   * @returns {boolean}
   */
  hasSeen(storageKey) {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(storageKey) === 'true';
    } catch (e) {
      return false;
    }
  }

  /**
   * Mark a tour as completed in localStorage
   * @param {string} storageKey
   */
  markSeen(storageKey) {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (e) {
      console.warn('[TourService] Failed to persist tour status:', e);
    }
  }

  /**
   * Reset completion status of a tour
   * @param {string} storageKey
   */
  reset(storageKey) {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn('[TourService] Failed to reset tour status:', e);
    }
  }

  /**
   * Start a tour using a declarative tourConfig and translation helper
   * @param {Object} tourConfig - { id, storageKey, steps }
   * @param {Object} options - { t, force, onComplete, onClose, ... }
   */
  start(tourConfig, options = {}) {
    const { t = (k, fallback) => fallback || k, force = false } = options;

    if (!force && tourConfig.storageKey && this.hasSeen(tourConfig.storageKey)) {
      return false;
    }

    if (!tourConfig || !Array.isArray(tourConfig.steps) || tourConfig.steps.length === 0) {
      console.warn('[TourService] Invalid tour configuration:', tourConfig);
      return false;
    }

    // Clean up any previously active tour before starting
    if (this.engine && this.engine.isActive()) {
      try {
        this.engine.destroy();
      } catch (e) {
        console.warn('[TourService] Error cleaning up previous tour:', e);
      }
    }

    // Format agnostic steps using i18n translation
    const formattedSteps = tourConfig.steps.map(step => ({
      ...step,
      title: step.titleKey ? t(step.titleKey, step.titleFallback || '') : (step.title || ''),
      description: step.descriptionKey ? t(step.descriptionKey, step.descriptionFallback || '') : (step.description || '')
    }));

    const engineOptions = {
      popoverClass: 'mytriad-tour-popover',
      nextBtnText: t('tour.buttons.next', 'Avançar'),
      prevBtnText: t('tour.buttons.prev', 'Voltar'),
      doneBtnText: t('tour.buttons.done', 'Concluir'),
      skipBtnText: t('tour.buttons.skip', 'Pular'),
      closeBtnText: t('tour.buttons.close', 'Fechar'),
      allowClose: true,
      overlayClickBehavior: 'close',
      onComplete: () => {
        if (tourConfig.storageKey) {
          this.markSeen(tourConfig.storageKey);
        }
        if (options.onComplete) {
          options.onComplete();
        }
      },
      onClose: () => {
        if (tourConfig.storageKey) {
          this.markSeen(tourConfig.storageKey);
        }
        if (options.onClose) {
          options.onClose();
        }
      },
      ...options
    };

    this.engine.drive(formattedSteps, engineOptions);
    return true;
  }

  /**
   * Destroy any currently active tour
   */
  destroy() {
    if (this.engine) {
      this.engine.destroy();
    }
  }

  /**
   * Check if a tour is active
   */
  isActive() {
    return this.engine ? this.engine.isActive() : false;
  }
}

export const tourService = new TourService();
export default tourService;
